import { useContext, useEffect, useState } from "react"
import * as polyline from "@mapbox/polyline"
import AdHocCommands from "@/api/adHocCommands"
import DriverPolylineParser from "@/lib/driverPolylineParser"
import { XmppContext } from "@/stream/xmppClient"

const EMPTY: [number, number][] = []

/** The trail covers at most this long, so an order whose timestamp is far in
 * the past (or bogus) cannot pull days of pings into one reply. */
export const MAX_TRAIL_WINDOW_MS = 3 * 24 * 60 * 60 * 1000

type Trail = { key: string; points: [number, number][] }

/** The order's start as an ISO string, or undefined if it isn't a usable
 * epoch-ms timestamp. */
const sinceOf = (orderTimestamp: string | undefined) => {
  if (!orderTimestamp || !/^\d+$/.test(orderTimestamp)) return undefined
  const date = new Date(Number(orderTimestamp))
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/** The path a driver has driven since the current delivery order began (at
 * most three days back from its end), as `[lng, lat]` points. Fetched once per driver and order, then extended with
 * `currentLocation` as it moves, unless `endsAt` closed the order. An empty
 * array means there is no line. */
export function useDrivenTrail({
  driverJid,
  orderTimestamp,
  enabled,
  currentLocation,
  endsAt,
}: {
  driverJid: string | undefined
  orderTimestamp: string | undefined
  enabled: boolean
  currentLocation: [number, number] | undefined
  endsAt?: string
}): [number, number][] {
  const { client } = useContext(XmppContext)
  const since = enabled ? sinceOf(orderTimestamp) : undefined
  const key =
    driverJid && since ? `${driverJid}|${since}|${endsAt ?? ""}` : undefined

  const [trail, setTrail] = useState<Trail | null>(null)

  useEffect(() => {
    if (!client || !driverJid || !since || !key) {
      setTrail(null)
      return
    }
    let cancelled = false
    // Clamped here rather than in `since` so the key, and with it the fetch,
    // stays fixed for the order instead of moving with the clock.
    const end = endsAt ? Date.parse(endsAt) : Date.now()
    const windowStart = new Date(
      Math.max(Date.parse(since), end - MAX_TRAIL_WINDOW_MS),
    ).toISOString()
    AdHocCommands.getDriverPolylines(
      client,
      driverJid,
      windowStart,
      new Date(end).toISOString(),
    )
      .then((result) => {
        if (cancelled) return
        const encoded = DriverPolylineParser.parse(result)?.polyline
        setTrail({
          key,
          points: encoded
            ? polyline
                .decode(encoded)
                .map(([lat, lng]): [number, number] => [lng, lat])
            : [],
        })
      })
      .catch(() => {
        if (!cancelled) setTrail({ key, points: [] })
      })
    return () => {
      cancelled = true
    }
  }, [client, driverJid, since, endsAt, key])

  const lng = endsAt ? undefined : currentLocation?.[0]
  const lat = endsAt ? undefined : currentLocation?.[1]
  const fetchedKey = trail?.key
  useEffect(() => {
    if (lng === undefined || lat === undefined) return
    setTrail((prev) => {
      if (!prev || prev.key !== key) return prev
      const last = prev.points[prev.points.length - 1]
      if (last && last[0] === lng && last[1] === lat) return prev
      return { key: prev.key, points: [...prev.points, [lng, lat]] }
    })
  }, [lng, lat, key, fetchedKey])

  return key && trail?.key === key ? trail.points : EMPTY
}
